import type { NormalizedStatement, NormalizedTransaction } from "./index.js";

/**
 * Deterministic guard against invented figures: every amount and every date in
 * an extracted row must appear verbatim in the statement's own (redacted) text
 * layer. Rows that fail are flagged for the user, never silently dropped.
 */

export interface RowProblem {
  row: number;
  field: "date" | "amountCents";
  message: string;
}

const compact = (s: string) => s.replace(/\s+/g, " ");

export function formatAmountCandidates(amountCents: number): string[] {
  const abs = Math.abs(amountCents);
  const dollars = Math.floor(abs / 100);
  const cents = String(abs % 100).padStart(2, "0");
  const plain = `${dollars}.${cents}`;
  const grouped = `${dollars.toLocaleString("en-US")}.${cents}`;
  // Some issuers print amounts under a dollar without the leading zero: ".52".
  const bare = dollars === 0 ? [`.${cents}`] : [];
  return [...new Set([plain, grouped, ...bare])];
}

export function formatDateCandidates(iso: string): string[] {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return [];
  const mm = m;
  const dd = d;
  const m1 = String(Number(m));
  const d1 = String(Number(d));
  const yy = y.slice(2);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const mon = months[Number(m) - 1] ?? "";
  return [
    `${mm}/${dd}`,
    `${m1}/${d1}`,
    `${mm}/${dd}/${yy}`,
    `${mm}/${dd}/${y}`,
    `${m1}/${d1}/${yy}`,
    `${m1}/${d1}/${y}`,
    `${mon} ${d1}`,
    `${mon} ${dd}`,
    `${y}-${mm}-${dd}`,
  ];
}

export function verifyVerbatim(statement: NormalizedStatement, text: string): RowProblem[] {
  const haystack = compact(text);
  const problems: RowProblem[] = [];
  statement.transactions.forEach((t: NormalizedTransaction, row: number) => {
    if (!formatDateCandidates(t.date).some((c) => haystack.includes(c))) {
      problems.push({
        row,
        field: "date",
        message: `The date ${t.date} is not printed anywhere on the statement, so it may have been misread. Check it against the page, fix it, or drop the row.`,
      });
    }
    if (!formatAmountCandidates(t.amountCents).some((c) => haystack.includes(c))) {
      problems.push({
        row,
        field: "amountCents",
        message: `The amount $${formatAmountCandidates(t.amountCents)[0]} is not printed anywhere on the statement, so it may have been misread. Check it against the page, fix it, or drop the row.`,
      });
    }
  });
  return problems;
}
